import { useCallback } from 'react';

import { getQueryClient } from '@bonusly/query-client';

import { useIdentityStore } from '../lib/store';
import { getMeQueryData, meQueryKey, useProfileQuery } from '../queries/profile';

export const useIdentity = () => {
	const { isLoading, error } = useProfileQuery();

	// Gate on Zustand flags rather than query isSuccess. The AuthProvider useEffect
	// writes both data and flags to Zustand atomically — using isSuccess here would
	// unblock AppLoading before the useEffect runs, causing components to read
	// undefined user/company from the store.
	const { isCompanyFetched, isUserFetched, areFeaturesFetched } = useIdentityStore();
	const isUserIdentified = isCompanyFetched && isUserFetched && areFeaturesFetched;

	// If Zustand is already populated (e.g., in tests), don't report loading
	const effectiveIsLoading = isUserIdentified ? false : isLoading;

	const refetchProfile = useCallback(async () => {
		const queryClient = await getQueryClient();
		await queryClient.invalidateQueries({ queryKey: meQueryKey });
		const data = getMeQueryData(queryClient);

		if (data) {
			useIdentityStore.setState({
				company: data.company,
				features: data.features,
				user: data.user,
			});

			return { company: data.company, user: data.user };
		}

		// Fallback: read from Zustand (for backward compat)
		const state = useIdentityStore.getState();
		return { company: state.company, user: state.user };
	}, []);

	return {
		error,
		isUserIdentified,
		isLoading: effectiveIsLoading,
		refetchProfile,
	};
};

// This is for async awaits outside of the React lifecycle
export const waitForAuthorization = async () => {
	// Check Zustand first (backward compat with tests)
	const state = useIdentityStore.getState();
	const isUserIdentified = state.isCompanyFetched && state.isUserFetched && state.areFeaturesFetched;

	if (isUserIdentified) {
		return true;
	}

	// Also check React Query cache
	const queryClient = await getQueryClient();
	if (getMeQueryData(queryClient)) {
		return true;
	}

	return new Promise<boolean>((resolve) => {
		// Listen to both Zustand and query cache
		const checkDone = () => {
			const state = useIdentityStore.getState();
			if (state.isCompanyFetched && state.isUserFetched && state.areFeaturesFetched) {
				return true;
			}
			if (getMeQueryData(queryClient)) {
				return true;
			}
			return false;
		};

		const unsubscribeStore = useIdentityStore.subscribe(() => {
			if (checkDone()) {
				unsubscribeStore();
				unsubscribeCache();
				resolve(true);
			}
		});

		const unsubscribeCache = queryClient.getQueryCache().subscribe(() => {
			if (checkDone()) {
				unsubscribeStore();
				unsubscribeCache();
				resolve(true);
			}
		});
	});
};
